/* global console */
import { aiDraft, AiDraftRequest, AiDraftOption } from "../services/ai.service";
import { convertMarkdownToHtml } from "../utils/markdown.utils";

export interface DraftFullDocumentResult {
  response: string;
  thread_id?: string;
  stopped: boolean;
  truncated: boolean;
}

// Safety fuse for a runaway loop only — NOT a product cap. The AI is expected
// to emit `stopped=true` when the document is complete. If it never does, this
// prevents burning credits indefinitely. A real legal draft will finish well
// below this number; hitting it means something is genuinely broken.
const RUNAWAY_SAFETY_LIMIT = 1000;

export async function processDraftFullDocument(
  request: AiDraftRequest
): Promise<DraftFullDocumentResult> {
  const outlineResponse = await aiDraft(AiDraftOption.DraftOutline, request);

  // Bail out before the section loop if the AI failed to produce an outline.
  // Otherwise every one of the RUNAWAY_SAFETY_LIMIT iterations would send
  // `outline: ""` to the draft endpoint, burning credits and potentially
  // inserting outline-free AI content into a legal document.
  if (outlineResponse.response == null || outlineResponse.response.trim() === "") {
    console.warn("[AILA] DraftFullDocument received an empty outline — aborting");
    return {
      response: "",
      thread_id: outlineResponse.thread_id,
      stopped: true,
      truncated: true,
    };
  }

  const outlineHtml = convertMarkdownToHtml(outlineResponse.response);

  const baseDocumentRequest: AiDraftRequest = {
    document_id: request.document_id,
    text_selected: request.text_selected,
    outline: outlineHtml,
    document_length: "short",
  };

  const sections: string[] = [];
  let threadId: string | undefined = outlineResponse.thread_id;
  let nextSection = false;

  for (let iteration = 0; iteration < RUNAWAY_SAFETY_LIMIT; iteration += 1) {
    const documentRequest: AiDraftRequest = { ...baseDocumentRequest, next_section: nextSection };
    if (threadId != null) documentRequest.thread_id = threadId;

    let documentResponse;
    try {
      documentResponse = await aiDraft(AiDraftOption.DraftFullDocument, documentRequest);
    } catch (error) {
      console.error("[AILA] fetchSection aiDraft error, returning partial content:", error);
      return { response: sections.join("\n"), thread_id: threadId, stopped: true, truncated: true };
    }

    sections.push(documentResponse.response);
    threadId = documentResponse.thread_id ?? threadId;
    const isStopped = documentResponse.stopped === true || documentResponse.stopped === "true";
    if (isStopped) {
      return {
        response: sections.join("\n"),
        thread_id: threadId,
        stopped: true,
        truncated: false,
      };
    }

    nextSection = true;
  }

  console.warn("[AILA] DraftFullDocument hit runaway safety limit — truncating");
  return { response: sections.join("\n"), thread_id: threadId, stopped: true, truncated: true };
}
